/**
 * What the alternatives actually do.
 *
 * The first draft of this table compared us with "a general drive" and claimed
 * it recognises faces. It does not — a *photo service* does, and a drive is a
 * file store that has never looked at a face in its life. The two got conflated
 * and the table said something untrue about a competitor, which is both
 * dishonest and the sort of claim that attracts a letter.
 *
 * So: two columns for the two products people actually use, and a rule for
 * every cell — it has to be true of the category as a whole, and defensible
 * without naming a brand. Where a category is split (a photo service will keep
 * your original if you pay for it), the cell says so rather than picking the
 * unflattering half.
 *
 * Nothing here is sourced from a competitor's marketing. If a claim cannot be
 * checked by opening the product, it is not in the table.
 */

export type Verdict = "no" | "partial" | "yes";

export interface ComparisonRow {
  question: string;
  /** Names the icon drawn beside the row. See `icons.tsx`. */
  icon:
    | "lock"
    | "library"
    | "sliders"
    | "faces"
    | "people"
    | "copy"
    | "crop"
    | "link"
    | "export"
    | "shield"
    | "video";
  drive: { verdict: Verdict; note: string };
  photos: { verdict: Verdict; note: string };
  ours: { verdict: Verdict; note: string };
}

export const COMPARISON: ComparisonRow[] = [
  {
    icon: "lock",
    /*
     * A topic, not a question. As "Can the provider read your photos?" the
     * tick beside our column read, in an accessible name, as "Yes — they can",
     * and the crosses beside the other two as "No — they cannot". Every other
     * row is a topic, so this one becomes one too.
     */
    question: "Keeping the provider out of your photos",
    drive: {
      verdict: "no",
      note: "Encrypted in transit and at rest, with a key they hold",
    },
    photos: {
      verdict: "no",
      note: "Same, and they read them to build search and tagging",
    },
    ours: {
      verdict: "yes",
      note: "Made on your device, and the file is encrypted before it is uploaded",
    },
  },
  {
    icon: "sliders",
    question: "Editing, without leaving",
    drive: {
      verdict: "no",
      note: "Download it, edit it elsewhere, upload it again",
    },
    photos: { verdict: "yes", note: "Crop and adjust, saved to their servers" },
    ours: {
      verdict: "yes",
      note: "Crop, straighten, exposure, colour — in your browser, original untouched",
    },
  },
  {
    icon: "faces",
    question: "Finding a particular person",
    drive: { verdict: "no", note: "A drive does not look at pictures" },
    photos: { verdict: "partial", note: "Yes — recognised on their servers" },
    ours: {
      verdict: "yes",
      note: "Recognised by your browser. No face ever reaches us",
    },
  },
  {
    icon: "shield",
    question: "Deciding who in the group can do what",
    drive: { verdict: "partial", note: "View or edit, per file or folder" },
    photos: { verdict: "partial", note: "Contributor or viewer, per album" },
    ours: {
      verdict: "yes",
      note: "Three roles and ten switches — who uploads, deletes, invites, shares",
    },
  },
  {
    icon: "library",
    question: "How many photos it holds",
    drive: { verdict: "yes", note: "As many as your plan has room for" },
    photos: {
      verdict: "partial",
      note: "A shared album is commonly capped in the thousands",
    },
    ours: {
      verdict: "yes",
      note: "No count limit at all. The only ceiling is the storage total",
    },
  },
  {
    icon: "people",
    question: "How many people can be in it",
    drive: {
      verdict: "partial",
      note: "Per-file sharing, one person at a time",
    },
    photos: { verdict: "partial", note: "A capped list of subscribers" },
    ours: {
      verdict: "yes",
      note: "No limit, and any of them can be given the invite",
    },
  },
  {
    icon: "crop",
    question: "Albums inside the shared space",
    drive: { verdict: "yes", note: "Folders, as many as you like" },
    photos: {
      verdict: "no",
      note: "A shared album is one flat album. That is the whole unit",
    },
    ours: {
      verdict: "yes",
      note: "As many albums as the trip needs, all inside one group",
    },
  },
  {
    icon: "copy",
    question: "Clearing out duplicates",
    drive: { verdict: "no", note: "Identical filenames at best" },
    photos: {
      verdict: "no",
      note: "Nothing stops the same photo going in twice",
    },
    ours: {
      verdict: "yes",
      note: "Caught on upload, and near-identical shots found later, side by side",
    },
  },
  {
    icon: "link",
    question: "Sharing with somebody outside",
    drive: {
      verdict: "partial",
      note: "A link anybody who finds it can open",
    },
    photos: {
      verdict: "partial",
      note: "A link anybody who finds it can open",
    },
    ours: {
      verdict: "yes",
      note: "A link that carries its own key, expires, and can be revoked",
    },
  },
  {
    icon: "export",
    question: "Getting everything back out",
    drive: { verdict: "yes", note: "It is a drive. Drag it out" },
    photos: { verdict: "partial", note: "An export you request and wait for" },
    ours: { verdict: "yes", note: "Download the originals whenever you like" },
  },
];
